import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { register as supabaseRegister, fetchOwnLinkCode } from '../../data/supabase/authService';
import { createAndStoreRecoveryCode } from '../../data/supabase/recoveryCode';
import { ArrowLeft } from 'lucide-react';
import { suggestUsername, validateRegistrationInput, sanitizeDisplayName, sanitizeEmail, isUsernameAvailable } from '../logic/auth';
import { buildUsernameSuggestions } from '../logic/usernameSuggestions';
import { AccountsState } from '../types';
import type { CreateAccountDetails, CreateAccountOptions } from '../../app/store/types';
import { fetchVerifiedSchools, type RegistrySchool } from '../../data/supabase/schools';
import { DjayNarrator, DjayBubble, BrandLockup, getDjayRegistrationStep } from '../../mascot';
import { soundManager } from '../../utils/soundManager';
import { hapticManager } from '../../utils/hapticManager';
import { dataLayer } from '../../data/dataLayer';
import { error as logError } from '../../utils/logger';
import { getRoleThemeClasses } from '../logic/uiHelpers';
import { RegisterRoleStep, RegisterNameStep, RegisterDetailsStep, RegisterPasswordStep, RegisterSuccessStep } from './register';
import type { SchoolMode } from './register/RegisterDetailsStep';
/**
 * RegisterScreen — Multi-step registration wizard guided by Djay.
 *
 * Flow: Role → Name → Details → Password → Success (with confetti!)
 *
 * Design principles:
 * - Student-first: Learner is pre-selected and most prominent.
 * - Djay as companion: Changes emotion and message per step
 * - Warm palette: Orange/amber matching the brand
 * - Playful progress: Dot indicators instead of a bar
 * - Celebration: Confetti burst on successful registration
 */
interface RegisterScreenProps {
  onRegisterSuccess: (accountId: string, role?: 'learner' | 'parent' | 'teacher' | 'admin') => void;
  onBack: () => void;
  onNavigateToLogin?: () => void;
  accounts?: AccountsState;
  createAccount: (role: 'learner' | 'teacher' | 'parent' | 'admin', details: CreateAccountDetails, options?: CreateAccountOptions) => string;
}
type Role = 'learner' | 'parent' | 'teacher';
type Step = 'role' | 'name' | 'details' | 'password' | 'success';
const STEPS: Step[] = ['role', 'name', 'details', 'password'];
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0
  })
};
export function RegisterScreen({
  onRegisterSuccess,
  onBack,
  onNavigateToLogin,
  accounts,
  createAccount
}: RegisterScreenProps) {
  const [currentStep, setCurrentStep] = useState<Step>('role');
  const [role, setRole] = useState<Role>('learner');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [grade, setGrade] = useState('6');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [generatedUsername, setGeneratedUsername] = useState('');
  const [generatedLinkCode, setGeneratedLinkCode] = useState('');
  const [generatedRecoveryCode, setGeneratedRecoveryCode] = useState('');
  const [schoolMode, setSchoolMode] = useState<SchoolMode>('unset');
  const [schoolSearch, setSchoolSearch] = useState('');
  const [schoolIsRequest, setSchoolIsRequest] = useState(false);
  /*
   Het id van de GEKOZEN school (migratie 0041). Leeg bij een aanvraag.
    Dit staat los van `schoolName` omdat een beheerder de twee moet kunnen
   onderscheiden: een naam die de aanvrager zelf opschreef is een bewering, een
   verwijzing naar een rij uit ons register is natrekbaar. Zolang ze in één veld
   zaten, was verifiëren een handeling die eruitzag als een controle.
    `schoolIsRequest` blijft ernaast bestaan en gaat over de UI-toestand: het
   bepaalt welke tekst de leerkracht leest. Dit veld gaat naar de server.
   */
  const [schoolId, setSchoolId] = useState('');
  const [copied, setCopied] = useState(false);
  const [recoveryCopied, setRecoveryCopied] = useState(false);
  // Gezet wanneer het inloggen wacht op een bevestiging van het kind in plaats
  // van op een timer (zie de herstelcode hieronder).
  const [pendingAccountId, setPendingAccountId] = useState<string | null>(null);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);
  /**
   * Het scholenregister.
   *
   * Stond hier eerst als `getUniqueSchoolNames(accounts)` — de scholen die
   * ándere leerkrachten op dit apparaat hadden ingetypt. Dat is geen lijst maar
   * een echo: wie als eerste "de regenboog" typte, bepaalde hoe de school
   * voortaan heette, en op een vers apparaat was de lijst gewoon leeg.
   *
   * Nu van de server, en alleen scholen die wij hebben opgenomen. Bestaat het
   * register nog niet, dan is de lijst leeg en loopt iedereen via de
   * aanvraagroute — precies de bedoeling, want zelf toevoegen mag niet meer.
   */
  const [registrySchools, setRegistrySchools] = useState<RegistrySchool[]>([]);
  const [registryLoading, setRegistryLoading] = useState(false);
  useEffect(() => {
    if (role !== 'teacher' || currentStep !== 'details') return;
    let cancelled = false;
    setRegistryLoading(true);
    void fetchVerifiedSchools().then(schools => {
      if (cancelled) return;
      setRegistrySchools(schools);
      setRegistryLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [role, currentStep]);
  /**
   * De gebruikersnaam die de SERVER zojuist weigerde.
   *
   * De lokale beschikbaarheidscontrole hieronder kijkt alleen in deze browser
   * en liegt daardoor in twee richtingen: een naam die een ander kind elders
   * al heeft lijkt vrij, en een naam die alleen hier bestaat lijkt bezet. De
   * server is de enige die het echt weet, en die zegt het pas bij verzenden.
   *
   * Een echte controle vooraf kan niet: een open endpoint dat vertelt of een
   * gebruikersnaam bestaat, vertelt daarmee ook welke kinderen een account
   * hebben. Dus vangen we de weigering op in plaats van hem te voorkomen.
   */
  const [rejectedUsername, setRejectedUsername] = useState('');
  const usernameAvailable = useMemo(() => {
    if (role !== 'learner' || !name.trim() || !accounts) return true;
    // Een serverweigering weegt zwaarder dan de lokale controle: die kijkt in
    // deze browser, de server kijkt in de werkelijkheid.
    if (rejectedUsername) return false;
    return isUsernameAvailable(accounts, suggestUsername(name));
  }, [role, name, accounts, rejectedUsername]);

  /**
   * Door het kind gekozen alternatief, als de kale voornaam bezet was.
   *
   * Leeg betekent "gebruik de voornaam". Wordt gewist zodra het kind zijn naam
   * aanpast: een gekozen `pietje-tijger` hoort niet te blijven staan als de naam
   * inmiddels Sanne is.
   */
  const [chosenUsername, setChosenUsername] = useState('');
  const handleNameChange = (value: string) => {
    setName(value);
    if (chosenUsername) setChosenUsername('');
    // Andere naam, andere gebruikersnaam — de oude weigering zegt niets meer.
    if (rejectedUsername) setRejectedUsername('');
  };
  const handlePickSuggestion = (username: string) => {
    setChosenUsername(username);
    // Het kind heeft een alternatief gekozen; de weigering is afgehandeld.
    if (rejectedUsername) setRejectedUsername('');
  };

  /**
   * Drie alternatieven, gebaseerd op de voornaam.
   *
   * Alleen berekend als het nodig is. `useMemo` op de naam en niet op elke
   * toetsaanslag opnieuw, anders veranderen de voorstellen onder de vinger van
   * het kind terwijl het aan het kiezen is.
   */
  const usernameSuggestions = useMemo(() => {
    if (role !== 'learner' || usernameAvailable) return [];
    return buildUsernameSuggestions(name, {
      includePlain: false,
      count: 3
      // De zojuist geweigerde naam nooit opnieuw voorstellen. Dat zou het kind
      // in een lus zetten met een knop die gegarandeerd weer faalt.
    }).filter(s => s !== rejectedUsername);
  }, [role, name, usernameAvailable, rejectedUsername]);
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setValidationError('');
    if (password !== passwordConfirm) {
      setValidationError('Wachtwoorden komen niet overeen.');
      soundManager.playError();
      hapticManager.hapticError();
      return;
    }
    if (accounts) {
      const error = validateRegistrationInput(role, {
        name: sanitizeDisplayName(name),
        email: role !== 'learner' ? sanitizeEmail(email) : undefined,
        password,
        schoolName: role === 'teacher' ? schoolName : undefined
      }, accounts);
      if (error) {
        setValidationError(error);
        soundManager.playError();
        hapticManager.hapticError();
        return;
      }
    }
    setIsSubmitting(true);
    try {
      const sanitizedName = sanitizeDisplayName(name);
      // Het gekozen alternatief heeft voorrang. Zo blijft de weergavenaam
      // "Pietje" terwijl de gebruikersnaam `pietje-tijger` is — het kind hoeft
      // zijn eigen naam niet aan te passen omdat iemand anders die al had.
      const username = role === 'learner' ? chosenUsername || suggestUsername(sanitizedName) : undefined;
      if (username) setGeneratedUsername(username);
      // Hier werd eerder alvast een koppelcode verzonnen. Die was betekenisloos:
      // de database maakt serverside een andere aan, en de echte wordt verderop
      // opgehaald. Een code die alleen in deze browser bestaat, is precies wat
      // een kind eerder liet opschrijven wat nergens bij hoorde.

      // ── Supabase-route ───────────────────────────────────────
      // De signup-trigger maakt de profielrij aan. Rol en groep gaan mee als
      // metadata, maar de database klemt 'admin' eruit — rolescalatie via het
      // registratieformulier is dus onmogelijk.
      //
      // Er gaat bewust GEEN `data_controller` mee: de database valt dan terug
      // op 'self', de strengste stand, waarbij ouderlijke toestemming vereist
      // is voordat er iets naar de server mag. Een schoolaccount — dat die
      // toestemming niet nodig heeft — kan zo niet per ongeluk ontstaan.
      //
      // Er was hier ook een lokale registratieroute met een eigen, ongesalte
      // SHA-256-hash. Die is weg: een tweede manier om een account te maken die
      // geen profielrij op de server oplevert, produceert spookaccounts die er
      // in de app volwaardig uitzien.
      const result = await supabaseRegister({
        role,
        displayName: sanitizedName,
        password,
        username,
        email: role !== 'learner' ? sanitizeEmail(email) : undefined,
        grade: role === 'learner' ? parseInt(grade, 10) : undefined,
        // Stond hier niet, terwijl het scherm de school wél vraagt. De naam
        // werd alleen lokaal gevalideerd en daarna weggegooid — zie 0038.
        //
        // Sinds 0041 sluiten een KEUZE en een AANVRAAG elkaar uit: bij een
        // gekozen school gaat alleen het id mee. De naam zou daarnaast een
        // tweede bron van waarheid worden die stil kan afwijken zodra de school
        // in het register wordt hernoemd.
        schoolName: role === 'teacher' && !schoolId ? schoolName.trim() : undefined,
        schoolId: role === 'teacher' && schoolId ? schoolId : undefined
      });
      if (!result.ok) {
        // Een bezette gebruikersnaam is geen fout maar een keuze die opnieuw
        // gemaakt moet worden. Het kind terugsturen naar de naamstap mét drie
        // nieuwe voorstellen is het enige antwoord waar het iets mee kan; een
        // rode melding op het wachtwoordscherm laat het raden wat er nu precies
        // mis is aan een naam die het niet meer ziet.
        const taken = (result.message ?? '').toLowerCase().includes('bezet');
        if (taken && role === 'learner' && username) {
          setRejectedUsername(username);
          setChosenUsername('');
          setValidationError('');
          setCurrentStep('name');
          soundManager.playError();
          hapticManager.hapticError();
          return;
        }
        setValidationError(result.message ?? 'Registreren is niet gelukt. Probeer het opnieuw.');
        soundManager.playError();
        hapticManager.hapticError();
        return;
      }

      // Zonder sessie staat e-mailconfirmatie aan: het account bestaat, maar
      // de gebruiker moet eerst zijn mailbox openen. Dat is geen fout, dus
      // tonen we het als bericht in plaats van als mislukking.
      if (!result.session) {
        setValidationError(result.message ?? 'Check je mailbox om je account te bevestigen.');
        return;
      }

      // De ECHTE koppelcode ophalen voordat we het succesScherm tonen. Een
      // lokaal verzonnen code is betekenisloos: de `handle_new_user()`-trigger
      // maakt serverside een andere aan. Het kind kreeg dus een code te zien
      // die bij geen enkel profiel hoorde — en merkte dat pas als zijn ouder er
      // weken later mee probeerde te koppelen.
      //
      // Mislukt het ophalen, dan tonen we liever geen code dan een verkeerde:
      // het succesScherm laat het blok dan weg, en de code staat sowieso in de
      // instellingen van het kind.
      let recovery: string | null = null;
      if (role === 'learner') {
        const realCode = await fetchOwnLinkCode();
        setGeneratedLinkCode(realCode ?? '');

        // Het vangnet voor een kind zonder e-mailadres. Dit kan pas hier: de
        // RPC werkt op auth.uid(), dus er moet een sessie zijn.
        //
        // Mislukt het, dan tonen we géén code. Een opgeschreven herstelcode die
        // de server niet kent, is erger dan geen code — het kind denkt dan
        // beschermd te zijn en ontdekt het tegendeel pas op het moment dat het
        // er echt op aankomt.
        recovery = await createAndStoreRecoveryCode();
        setGeneratedRecoveryCode(recovery ?? '');

        // De rondleiding aanzetten. Dit stond in `OnboardingWelcomeStep`, maar
        // die stap wordt sinds de omzetting naar Supabase-auth nooit meer
        // getoond: `applyProfileToAccounts` zet `hasCompletedOnboarding` op
        // `true` zodra de sessie tot stand komt, dus `AuthGuard` sloeg de wizard
        // over en niemand zette de vlag ooit. Een kind kwam daardoor zonder
        // enige uitleg op het dashboard.
        //
        // Hier is ook de betere plek: de rondleiding gaat over de app, niet
        // over het aanmaken van een account. Een extra wizard vóór het
        // dashboard is een derde poort na het formulier en het succesScherm.
        try {
          const prefs = await dataLayer.getLearnerPreferences();
          await dataLayer.saveLearnerPreferences({
            ...prefs,
            pendingAppTour: true,
            tourCompleted: false
          });
        } catch (err) {
          // Niet fataal: zonder rondleiding werkt de app gewoon, en hij is
          // opnieuw te starten via de instellingen.
          logError('[RegisterScreen] rondleiding aanzetten mislukt', err);
        }
      }
      setCurrentStep('success');
      soundManager.playLevelUp();
      hapticManager.hapticSuccess();
      const newId = result.session.accountId;
      // Staat er een herstelcode op het scherm, dan géén aftelklok. Die code is
      // éénmalig: hij staat nergens anders en is ook door ons niet terug te
      // lezen. Hem na een paar seconden wegschuiven terwijl een kind hem nog
      // aan het overschrijven is, vernietigt precies het vangnet dat we net
      // hebben aangelegd. Het kind bevestigt zelf.
      if (recovery) {
        setPendingAccountId(newId);
      } else {
        successTimeoutRef.current = setTimeout(() => onRegisterSuccess(newId, role), role === 'learner' ? 6000 : 3500);
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const nextStep = () => {
    soundManager.playClick();
    hapticManager.hapticLight();
    if (currentStep === 'role') setCurrentStep('name');else if (currentStep === 'name') setCurrentStep('details');else if (currentStep === 'details') setCurrentStep('password');
  };
  const prevStep = () => {
    soundManager.playClick();
    hapticManager.hapticLight();
    setValidationError('');
    if (currentStep === 'name') setCurrentStep('role');else if (currentStep === 'details') setCurrentStep('name');else if (currentStep === 'password') setCurrentStep('details');else if (currentStep === 'role') onBack();
  };
  const handleSelectRole = (r: Role) => {
    soundManager.playClick();
    hapticManager.hapticLight();
    setRole(r);
    setTimeout(nextStep, 250);
  };
  const handleCopyLinkCode = () => {
    soundManager.playClick();
    hapticManager.hapticLight();
    navigator.clipboard.writeText(generatedLinkCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleCopyRecoveryCode = () => {
    soundManager.playClick();
    hapticManager.hapticLight();
    navigator.clipboard.writeText(generatedRecoveryCode);
    setRecoveryCopied(true);
    setTimeout(() => setRecoveryCopied(false), 2000);
  };
  const theme = getRoleThemeClasses(role);
  const djayState = getDjayRegistrationStep(currentStep, name, role);
  const currentStepIndex = STEPS.indexOf(currentStep as Step);
  return <div className={`min-h-screen ${theme.background} flex items-center justify-center p-4 py-8 sm:py-12 overflow-hidden transition-colors duration-500 relative`} data-id="element-518" data-mp="xmdnffe55xj5">
      
      {/* Brand Header (Desktop) */}
      <div className="absolute top-6 left-6 hidden md:block z-20" data-id="element-519" data-mp="okcqkjmtldi0">
        <BrandLockup size="sm" data-id="element-520" data-mp="1435nexwojuri" />
      </div>

      <div className="w-full max-w-md relative z-10" data-id="element-521" data-mp="142498kkdas5k">
        {/* Back button */}
        {currentStep !== 'success' && <motion.button onClick={prevStep} className="flex items-center gap-2 text-slate-400 hover:text-slate-700 mb-5 transition-colors font-semibold text-sm" initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-522" data-mp="z1zt4vndfzfv">
          
            <ArrowLeft className="w-4 h-4" data-id="element-523" data-mp="l5cjbsablesj" /> Terug
          </motion.button>}

        {/* Djay companion */}
        <motion.div className="flex flex-col items-center mb-6" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} data-id="element-524" data-mp="gb8bz7x574qx">
          
          <motion.div animate={{
          y: [0, -8, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-525" data-mp="2e39fhwn0o0f">
            
            <DjayNarrator size="lg" emotion={currentStep === 'success' ? 'celebrating' : djayState.emotion} animate={true} data-id="element-526" data-mp="291spe5hx8vrg" />
            
          </motion.div>
          <div className="mt-3 w-full max-w-[260px]" data-id="element-527" data-mp="2dzc8bipkdwpq">
            <AnimatePresence mode="wait" data-id="element-528" data-mp="2izgpzc27kxhj">
              <motion.div key={`${currentStep}-${role}`} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.25
            }} data-id="element-529" data-mp="2j0i3dn6p5w2f">
                
                <DjayBubble message={djayState.text} position="top" autoDismissMs={0} colorScheme={theme.colorScheme} data-id="element-530" data-mp="1zc96x0y4f3x4" />
                
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Card */}
        <motion.div className="bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden relative" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.1
      }} data-id="element-531" data-mp="17kbilur4uarl">
          
          {/* Progress dots */}
          {currentStep !== 'success' && <div className="flex justify-center gap-2 pt-5 pb-1" data-id="element-532" data-mp="s158oxs042k7">
              {STEPS.map((step, i) => <motion.div key={step} className={`rounded-full transition-all duration-300 ${i <= currentStepIndex ? 'bg-orange-400' : 'bg-slate-200'}`} animate={{
            width: i === currentStepIndex ? 24 : 8,
            height: 8
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25
          }} data-id="element-533" data-mp="x0hs18jnyri1" data-mpi={i} />)}
            </div>}

          <div className="p-6 sm:p-8" data-id="element-534" data-mp="99cckuhmy189">
            <form onSubmit={currentStep === 'password' ? handleRegister : e => {
            e.preventDefault();
            nextStep();
          }} data-id="element-535" data-mp="e25g5f1db3ir">
              
              <AnimatePresence mode="wait" custom={1} data-id="element-536" data-mp="3hqz0wrybg2yw">
                {currentStep === 'role' && <RegisterRoleStep role={role} onSelectRole={handleSelectRole} slideVariants={slideVariants} data-id="element-537" data-mp="3how5fsee9hms" />}

                {currentStep === 'name' && <RegisterNameStep role={role} name={name} onNameChange={handleNameChange} usernameAvailable={usernameAvailable} suggestedUsername={suggestUsername(name)} suggestions={usernameSuggestions} onPickSuggestion={handlePickSuggestion} chosenUsername={chosenUsername} takenUsername={rejectedUsername} slideVariants={slideVariants} data-id="element-538" data-mp="357uzgruat8n7" />}

                {currentStep === 'details' && <RegisterDetailsStep role={role} grade={grade} onGradeChange={setGrade} email={email} onEmailChange={setEmail} schoolName={schoolName} onSchoolNameChange={setSchoolName} schoolSearch={schoolSearch} onSchoolSearchChange={setSchoolSearch} schoolMode={schoolMode} onSchoolModeChange={setSchoolMode} schoolIsRequest={schoolIsRequest} onSchoolIsRequestChange={setSchoolIsRequest} onSchoolIdChange={setSchoolId} registrySchools={registrySchools} registryLoading={registryLoading} onNext={nextStep} slideVariants={slideVariants} data-id="element-539" data-mp="2mhvh0nsyqrsr" />}

                {currentStep === 'password' && <RegisterPasswordStep role={role} password={password} passwordConfirm={passwordConfirm} validationError={validationError} isSubmitting={isSubmitting} onPasswordChange={v => {
                setPassword(v);
                setValidationError('');
              }} onPasswordConfirmChange={v => {
                setPasswordConfirm(v);
                setValidationError('');
              }} slideVariants={slideVariants} data-id="element-540" data-mp="1tu3vg97pv4bg" />}

                {currentStep === 'success' && <RegisterSuccessStep role={role} name={name} generatedUsername={generatedUsername} generatedLinkCode={generatedLinkCode} generatedRecoveryCode={generatedRecoveryCode} onCopyLinkCode={handleCopyLinkCode} onCopyRecoveryCode={handleCopyRecoveryCode} copied={copied} recoveryCopied={recoveryCopied} onContinue={pendingAccountId ? () => onRegisterSuccess(pendingAccountId, role) : undefined} data-id="element-541" data-mp="2hjof6ubp25ls" />}
              </AnimatePresence>
            </form>
          </div>
        </motion.div>

        {/* Cross-link to Login */}
        {currentStep !== 'success' && onNavigateToLogin && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="mt-6 text-center" data-id="element-542" data-mp="jxxa5nax70cp">
          
            <p className="text-slate-500 font-medium text-sm" data-id="element-543" data-mp="tngy37dhpkef">
              Heb je al een account?{' '}
              <button onClick={onNavigateToLogin} className="text-orange-600 font-bold hover:text-orange-800 hover:underline transition-colors" data-id="element-544" data-mp="5x4f91gpi392">
              
                Log hier in
              </button>
            </p>
          </motion.div>}
      </div>
    </div>;
}